import { useEffect, useRef, useState } from "react";
import { formatRelativeTime, formatSecondsCompact, getRemainingSeconds } from "@/app/time";
import { KidAdvancedActions } from "./KidAdvancedActions";
import { KidQuickActions } from "./KidQuickActions";
import type { KidStatusSummary, KidViewState } from "../types";

interface KidDeviceCardProps {
  kid: KidStatusSummary;
  uiState: KidViewState;
  nowMinuteMs: number;
  nowSecondMs: number;
  onQueueAdjust: (seconds: number, label: string) => void;
  onQueueSet: (seconds: number, label: string) => void;
}

export function KidDeviceCard({ kid, uiState, nowMinuteMs, nowSecondMs, onQueueAdjust, onQueueSet }: KidDeviceCardProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!uiState.flashToken) return;
    if (!cardRef.current) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    cardRef.current.animate(
      [
        { boxShadow: "0 0 0 0 rgba(59, 130, 246, 0.0)", transform: "scale(1)" },
        { boxShadow: "0 0 0 6px rgba(59, 130, 246, 0.12)", transform: "scale(1.01)" },
        { boxShadow: "0 0 0 0 rgba(59, 130, 246, 0.0)", transform: "scale(1)" },
      ],
      { duration: 520, easing: "ease-out" },
    );
  }, [uiState.flashToken]);

  const waitingToSync = kid.outstandingMessageCount;
  const hasPendingSync = waitingToSync > 0;
  const pendingDelta = uiState.optimisticDeltaSeconds;
  const activeSessionRemainingSeconds = kid.activeSession
    ? getRemainingSeconds(kid.activeSession.startAt, kid.activeSession.durationSeconds, nowSecondMs)
    : 0;
  const hasActiveSession = Boolean(kid.activeSession);
  const pendingDeltaLabel =
    pendingDelta === 0 ? "No local pending change" : `${pendingDelta > 0 ? "+" : ""}${formatSecondsCompact(pendingDelta)}`;

  return (
    <article
      ref={cardRef}
      className={`kid-device-card rounded-xl border bg-card p-4 shadow-sm transition-colors ${uiState.isQueueing ? "kid-device-card--queued" : ""}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold leading-tight">{kid.displayName}</h3>
          <p className="text-xs text-muted-foreground">Last updated {formatRelativeTime(kid.lastUpdatedAt, nowMinuteMs)}</p>
        </div>
        {uiState.isQueueing ? (
          <span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">Queued</span>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <button
          type="button"
          className="group text-left"
          onClick={() => setDetailsOpen(open => !open)}
          aria-expanded={detailsOpen}
        >
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Effective time left</p>
          <p className="text-3xl font-semibold leading-tight group-hover:underline">{formatSecondsCompact(kid.effectiveTimeLeftSeconds)}</p>
        </button>
        {hasActiveSession ? (
          <div className="min-w-[170px] rounded-md bg-muted/40 px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Active session</p>
            <p className="text-2xl font-semibold leading-tight text-muted-foreground">
              {formatSecondsCompact(activeSessionRemainingSeconds)}
            </p>
          </div>
        ) : null}
      </div>

      {detailsOpen ? (
        <div className="mt-2 grid gap-1 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
          <p>Base: {formatSecondsCompact(kid.baseTimeLeftSeconds)}</p>
          <p>Pending local change: {pendingDeltaLabel}</p>
          <p>Effective total: {formatSecondsCompact(kid.effectiveTimeLeftSeconds)}</p>
          {uiState.lastQueuedAt ? <p>Last queued {formatRelativeTime(uiState.lastQueuedAt, nowMinuteMs)}</p> : null}
        </div>
      ) : null}

      {hasPendingSync ? (
        <p className="mt-2 text-xs font-medium text-amber-700">Waiting for device to connect</p>
      ) : null}

      <div className="mt-4 grid gap-3">
        <KidQuickActions
          isBusy={uiState.isQueueing}
          isMoreOpen={advancedOpen}
          onAdd30={() => onQueueAdjust(30 * 60, "+30m")}
          onAdd60={() => onQueueAdjust(60 * 60, "+60m")}
          onToggleMore={() => setAdvancedOpen(open => !open)}
        />

        {advancedOpen ? (
          <KidAdvancedActions
            kidId={kid.kidId}
            isBusy={uiState.isQueueing}
            onAdd15={() => onQueueAdjust(15 * 60, "+15m")}
            onRemoveAll={() => onQueueSet(0, "Remove all")}
            onCustomAdjust={seconds => onQueueAdjust(seconds, `Adjust ${seconds}s`)}
            onSetTarget={seconds => onQueueSet(Math.max(0, seconds), `Set target ${Math.max(0, seconds)}s`)}
          />
        ) : null}
      </div>

      {uiState.lastQueueError ? <p className="mt-3 text-xs text-red-600">{uiState.lastQueueError}</p> : null}
    </article>
  );
}
